import React, { useState, useEffect } from 'react';
import { Alert, Button, Col, Container, DatePicker, Form, FormGroup, FormInput, FormSelect, InputGroup, InputGroupAddon, InputGroupText, Row } from 'shards-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import PageTitle from '../components/common/PageTitle';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import useGridOptionsInventoryStatus from '../hooks/grid/gridOptions/useGridOptionsInventoryStatus';
import commonColDef from '../data/grid/commonColDef';
import useColdefInventoryStatus from '../hooks/grid/coldef/useColdefInventoryStatus';
import { useTranslation } from 'react-i18next';
import useLocales from '../hooks/useLocales';
import { RootState, useDispatch, useSelector } from '../redux/store';
import { resetMessage, getInventoryStatuss, searchInventoryStatussByDate, searchInventoryStatussByJobId } from '../redux/slices/inventoryStatus';
import moment from 'moment';
import { DATE_COMPARISON_OPTIONS } from '../constants';
import type { ColDef, GridApi, ColumnApi, GridReadyEvent } from 'ag-grid-community';
import type { DateComparisonOperator, JobId } from '../types/inventoryStatus';

const ManageInventoryStatus = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const { hasError, isLoading, serverMessage, inventoryStatusRowData, jobIdOptions } = useSelector((state: RootState) => state.inventoryStatus);
  const { users } = useSelector((state: RootState) => state.user);
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsInventoryStatus();
  const customColDef: ColDef = {
    flex: 1,
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const coldefInventoryStatus = useColdefInventoryStatus(t);
  const frameworkComponents = {};
  const [inventoryJobIdOption, setInventoryJobIdOption] = useState<number>(null);
  const [dateComparisonOption, setDateComparisonOption] = useState<DateComparisonOperator>('gte');
  const [dateInput, setDateInput] = useState<Date>(moment().toDate());
  const searchStatusByDateReady = dateComparisonOption.length && dateInput;
  const [filteringText, setFilteringText] = useState<string>('');
  const searchStatusByJobIdReady = jobIdOptions && jobIdOptions.length;

  /**
   * 確認用
   */
  // useEffect(() => {
  //   console.log(hasDateInputError);
  // }, [hasDateInputError]);

  useEffect(() => {
    // 初回データ取得
    dispatch(getInventoryStatuss());
  }, []);

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  /**
   * メッセージを消す
   */
  const dismiss = (): void => {
    dispatch(resetMessage());
  };

  /**
   * 棚卸ID選択
   */
  const handleSelectJobId = (event): void => {
    // console.log(event.target.value);
    setInventoryJobIdOption(event.target.value);
  };

  /**
   * 日付入力
   */
  const handleDateInput = (selectedDate: Date): void => {
    setDateInput(selectedDate);
  };

  /**
   * 日付比較選択
   */
  const handleSelectDateComparison = (event): void => {
    // console.log(event.target.value);
    setDateComparisonOption(event.target.value);
  };

  /**
   * ジョブ検索
   */
  const handleSearchStatusByJobId = (): void => {
    dispatch(resetMessage());
    dispatch(searchInventoryStatussByJobId(inventoryJobIdOption));
  };
  const handleSearchStatusByDate = (): void => {
    dispatch(resetMessage());
    const formattedDate: string = moment(dateInput).format('YYYY-MM-DD');
    dispatch(searchInventoryStatussByDate(formattedDate, dateComparisonOption));
  };

  /**
   * 検索リセット
   */
  const handleResetSearch = (): void => {
    dispatch(getInventoryStatuss());
  };

  /**
   * テキストフィルタリング
   */
  const handleSearchTextInput = (event): void => {
    const searchText: string = event.target.value;
    setFilteringText(searchText);
    if (gridApi) {
      gridApi.setQuickFilter(searchText);
    }
  };
  const handleClearFilteringText = (): void => {
    setFilteringText('');
    if (gridApi) {
      gridApi.setQuickFilter('');
    }
  };

  return (
    <Container fluid className="main-content-container px-4">
      <Row id="pageTitle" noGutters className="page-header py-4">
        <PageTitle sm="4" title={t('inventory.status')} subtitle="Inventory Status" className="text-sm-left" />
      </Row>

      <div id="success" className="pb-1">
        <Alert id="successServer" theme="success" dismissible={dismiss} open={!hasError && serverMessage !== undefined}>
          <span>{serverMessage}</span>
        </Alert>
      </div>

      <div id="alerts" className="pb-1">
        <Alert id="alertServer" theme="danger" dismissible={dismiss} open={hasError}>
          <span>{serverMessage}</span>
        </Alert>
      </div>

      <Row className="mb-2" form>
        <Col md="4">
          <InputGroup>
            <InputGroupAddon type="prepend">
              <InputGroupText>{t('filter.inventoryId')}</InputGroupText>
            </InputGroupAddon>
            <FormSelect onChange={handleSelectJobId}>
              {jobIdOptions &&
                jobIdOptions.map((item: JobId) => {
                  return (
                    <option key={'jobId' + item.jobId} value={item.jobId}>
                      {item.jobId}
                    </option>
                  );
                })}
            </FormSelect>
            <InputGroupAddon type="append">
              <Button size="sm" disabled={!searchStatusByJobIdReady} onClick={handleSearchStatusByJobId}>
                {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSearch} />}
                &nbsp;&nbsp;{t('filter.search')}
              </Button>
            </InputGroupAddon>
          </InputGroup>
        </Col>
        <Col md="4">
          <InputGroup>
            <InputGroupAddon type="prepend">
              <InputGroupText>{t('filter.initialContactDate')}</InputGroupText>
            </InputGroupAddon>
            <div style={{ width: '8rem' }}>
              <DatePicker selected={dateInput} onChange={handleDateInput} placeholderText="Select Date" dropdownMode="select" dateFormat="yyyy-MM-dd" className="text-center rounded-0" />
            </div>
            <FormSelect onChange={handleSelectDateComparison} defaultValue={'gte'}>
              {DATE_COMPARISON_OPTIONS.map((item) => {
                return (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                );
              })}
            </FormSelect>
            <InputGroupAddon type="append">
              <Button size="sm" disabled={!searchStatusByDateReady} onClick={handleSearchStatusByDate}>
                {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSearch} />}
                &nbsp;&nbsp;{t('filter.search')}
              </Button>
            </InputGroupAddon>
          </InputGroup>
        </Col>
        <Col md="2">
          <InputGroup>
            <Button theme="light" onClick={handleResetSearch}>
              <FontAwesomeIcon icon={faTimesCircle} />
              &nbsp;{t('filter.reset')}
            </Button>
          </InputGroup>
        </Col>
        <Col md="2">
          <InputGroup seamless={true}>
            <FormInput id="grid-search-text" onChange={handleSearchTextInput} placeholder={t('filter.default')} value={filteringText} />
            <InputGroupAddon type="append">
              <InputGroupText>
                <FontAwesomeIcon icon={faTimesCircle} onClick={handleClearFilteringText} style={{ cursor: 'pointer' }} />
              </InputGroupText>
            </InputGroupAddon>
          </InputGroup>
        </Col>
      </Row>

      <div className="area-exec">
        <Form>
          <FormGroup>
            <div className="ag-theme-balham" style={{ height: 'calc(100vh - 15em)' }}>
              <AgGridReact
                defaultColDef={defaultColDef}
                columnDefs={coldefInventoryStatus}
                gridOptions={gridOptions}
                rowData={inventoryStatusRowData}
                onGridReady={onGridReady}
                frameworkComponents={frameworkComponents}
                // filterValueGetterで使うデータ
                context={{
                  currentLang,
                  // reduxにあるものはコピーを渡す
                  users: users?.map((user) => structuredClone(user)),
                }}
              />
            </div>
          </FormGroup>
        </Form>
      </div>
    </Container>
  );
};

export default ManageInventoryStatus;
